import React, { Component } from "react";
import { Table } from "react-bootstrap";
import { connect } from "react-redux";
import moment from "moment";
import _ from "lodash";
import FontAwesome from "react-fontawesome";
import { Link } from "react-router-dom";
import { withRouter } from "react-router";
import {
	calendarsLoaded,
	bookingTypesLoaded,
	usersLoaded,
} from "../../actions";
import { bookingActions, userActions } from "../../webapi";
import { checkLoggedIn, validateAccess } from "../../session";
import { Text } from "../../components/text";
import { TIMESTAMPFORMAT, minutesToString } from "../../helper";
import {
	DatePicker,
	FilterMenu,
	GenericInput,
	Popup,
	Tag,
	UserListing,
} from "../../components";
import {
	COLOUR_GREEN,
	COLOUR_RED,
	COLOUR_TANGERINE,
	TEXT_LIGHT,
} from "../../js";
import BookingCalendar from "./BookingCalendar";
import BookingDayView from "./BookingDayView";

class Bookings extends Component {
	constructor(props) {
		super(props);

		// Define the default view (easily changeable)
		const defaultView = "list"; // Could be 'list', 'week', or 'calendar'

		// Adaptively set initial date range based on default view
		let startDate, endDate;

		switch (defaultView) {
			case "day":
				startDate = moment().format("YYYY-MM-DD");
				endDate = moment().format("YYYY-MM-DD");
				break;
			case "week":
				startDate = moment().startOf("week").format("YYYY-MM-DD");
				endDate = moment().endOf("week").format("YYYY-MM-DD");
				break;
			case "calendar":
				startDate = moment().startOf("month").format("YYYY-MM-DD");
				endDate = moment().endOf("month").format("YYYY-MM-DD");
				break;
			case "list":
			default:
				// List view shows upcoming bookings (next 7 days)
				startDate = moment().format("YYYY-MM-DD");
				endDate = moment().add(1, "w").format("YYYY-MM-DD");
				break;
		}

		this.state = {
			sortColumn: "Time", // column to sort by
			sortDesc: false, // if true, sort descending rather than ascending
			startDate,
			endDate,
			selectedView: defaultView,
			selectedDate: null,
			selectedStatusFilter: null, // Default: show all statuses
			statusFilterOpen: false,
			selectedUserFilter: null,
			userFilterOpen: false,
			selectedCalendarFilter: null,
			calendarFilterOpen: false,
			selectedTypeFilter: null,
			typeFilterOpen: false,
			timeFilterOpen: false,
			userSearch: "",
			bookings: [],
			loadingBookings: false,
		};
	}

	componentDidMount() {
		checkLoggedIn(this, this.props.auth);

		// Check permissions and redirect if user has no access
		if (
			!validateAccess(this.props.auth.site, "bookings", this.props.auth) &&
			!validateAccess(this.props.auth.site, "assignedBookings", this.props.auth)
		) {
			this.props.history.push("/mastermenu");
			return;
		}

		this.getData();
		this.getBookings();
	}

	getData() {
		bookingActions.getCalendarsBySite(this.props.auth.site).then((res) => {
			// Filter calendars based on user permissions
			const filteredCalendars = this.filterCalendarsByPermissions(
				res.data.Items,
			);
			this.props.calendarsLoaded(filteredCalendars);
		});

		bookingActions.getBookingTypesBySite(this.props.auth.site).then((res) => {
			this.props.bookingTypesLoaded(res.data.Items);
		});

		userActions.getSiteUsersRecursive(this.props.auth.site).then((users) => {
			this.props.usersLoaded(users);
		});
	}

	getBookings = (startDate = null, endDate = null) => {
		// Use provided dates or fall back to state
		const start = startDate || this.state.startDate;
		const end = endDate || this.state.endDate;

		// Prevent multiple concurrent API calls
		if (this.state.loadingBookings) {
			return;
		}

		this.setState({
			bookings: [],
			loadingBookings: true,
		});

		bookingActions
			.searchBookings(
				this.props.auth.site,
				moment(start).startOf("d").valueOf(),
				moment(end).endOf("d").valueOf(),
				this.state.selectedUserFilter && this.state.selectedUserFilter.userId,
				this.state.selectedCalendarFilter &&
					this.state.selectedCalendarFilter.Id,
				this.state.selectedTypeFilter && this.state.selectedTypeFilter.Id,
			)
			.then((res) => {
				// Filter bookings based on user permissions
				const filteredBookings = this.filterBookingsByPermissions(
					res.data.Items,
				);
				this.setState({
					bookings: filteredBookings,
					loadingBookings: false,
				});
			})
			.catch(() => {
				this.setState({
					bookings: [],
					loadingBookings: false,
				});
			});
	};

	updateView = (view) => {
		// Prevent calling if already in the same view
		if (this.state.selectedView === view) {
			return;
		}

		this.setState({ selectedView: view, selectedDate: null });

		// Load appropriate data for each view when switching
		if (view === "calendar") {
			const startDate = moment().startOf("month");
			const endDate = moment().endOf("month");
			this.setState({
				startDate: startDate.format("YYYY-MM-DD"),
				endDate: endDate.format("YYYY-MM-DD"),
			});
			this.getBookings(
				startDate.format("YYYY-MM-DD"),
				endDate.format("YYYY-MM-DD"),
			);
		} else if (view === "week") {
			const startDate = moment().startOf("week");
			const endDate = moment().endOf("week");
			this.setState({
				startDate: startDate.format("YYYY-MM-DD"),
				endDate: endDate.format("YYYY-MM-DD"),
			});
			this.getBookings(
				startDate.format("YYYY-MM-DD"),
				endDate.format("YYYY-MM-DD"),
			);
		} else if (view === "day") {
			const startDate = moment().format("YYYY-MM-DD");
			const endDate = moment().format("YYYY-MM-DD");
			this.setState({
				startDate,
				endDate,
			});
			this.getBookings(startDate, endDate);
		} else if (view === "list") {
			// List view shows upcoming bookings (today + 7 days)
			const startDate = moment().format("YYYY-MM-DD");
			const endDate = moment().add(1, "w").format("YYYY-MM-DD");
			this.setState(
				{
					startDate,
					endDate,
				},
				() => {
					this.getBookings();
				},
			);
		}
	};

	selectDate = (date) => {
		this.setState({ selectedDate: date });
	};

	onChangeMonth = (year, month) => {
		const startDate = moment()
			.year(year)
			.month(month - 1)
			.startOf("month");
		const endDate = moment()
			.year(year)
			.month(month - 1)
			.endOf("month");
		this.getBookings(
			startDate.format("YYYY-MM-DD"),
			endDate.format("YYYY-MM-DD"),
		);
	};

	onChangeWeek = (weekStart) => {
		const startDate = moment(weekStart).startOf("week");
		const endDate = moment(weekStart).endOf("week");
		this.getBookings(
			startDate.format("YYYY-MM-DD"),
			endDate.format("YYYY-MM-DD"),
		);
	};

	onChangeDay = (newDate) => {
		const dateStr = moment(newDate).format("YYYY-MM-DD");
		this.setState({
			startDate: dateStr,
			endDate: dateStr,
		});
		this.getBookings(dateStr, dateStr);
	};

	filterByStatus = (bookings) => {
		if (this.state.selectedStatusFilter) {
			return bookings.filter((b) => {
				if (this.state.selectedStatusFilter === "Cancelled") {
					return b.Cancelled === true;
				}
				const status = b.Status || "Confirmed";
				return status === this.state.selectedStatusFilter && !b.Cancelled;
			});
		}
		return bookings.filter((b) => !b.Cancelled);
	};

	getCalendarBookings = () => {
		if (!this.state.bookings || this.state.bookings.length === 0) {
			return [];
		}

		return this.filterByStatus(
			this.state.bookings.filter((booking) => booking && booking.Id),
		)
			.map((booking) => {
				try {
					const bookingTime = moment.parseZone(
						booking.TimeString || booking.Time,
					);
					return {
						Id: booking.Id,
						DateKey: bookingTime.format("DD-MM-YYYY"),
						Time:
							bookingTime.format("mm") === "00"
								? bookingTime.format("ha")
								: bookingTime.format("h:mma"),
						EndTime:
							booking.EndTimeString || booking.EndTime
								? moment
										.parseZone(booking.EndTimeString || booking.EndTime)
										.format("h:mma")
								: null,
						Title: `${booking.Id} - ${booking.BookingType && booking.BookingType.Title ? booking.BookingType.Title : "Unknown Type"}`,
						Location:
							booking.Calendar && booking.Calendar.Title
								? booking.Calendar.Title
								: "Unknown Calendar",
						CalendarId: booking.CalendarId,
						User: booking.User,
						BookedBy: booking.BookedBy,
						Status: booking.Status || "Confirmed",
						AllDay: false,
						Cancelled: booking.Cancelled || false,
						IsBlockOut: booking.BookingTypeId === "BLOCKOUT",
						RawTime: booking.Time,
					};
				} catch (error) {
					console.error(
						"Error transforming booking for calendar:",
						booking,
						error,
					);
					return null;
				}
			})
			.filter((booking) => booking !== null); // Remove any failed transformations
	};

	openUserFilter = () => {
		this.setState({
			userFilterOpen: true,
		});
	};

	closeUserFilter = () => {
		this.setState({
			userFilterOpen: false,
		});
	};

	selectUserFilter = (user) => {
		this.setState(
			{
				selectedUserFilter: user,
				userFilterOpen: false,
			},
			this.getBookings,
		);
	};

	removeUserFilter = (event) => {
		event.stopPropagation();
		this.setState(
			{
				selectedUserFilter: null,
			},
			this.getBookings,
		);
	};

	openCalendarFilter = () => {
		this.setState({
			calendarFilterOpen: true,
		});
	};

	closeCalendarFilter = () => {
		this.setState({
			calendarFilterOpen: false,
		});
	};

	selectCalendarFilter = (calendar) => {
		this.setState(
			{
				selectedCalendarFilter: calendar,
				calendarFilterOpen: false,
			},
			this.getBookings,
		);
	};

	removeCalendarFilter = (event) => {
		event.stopPropagation();
		this.setState(
			{
				selectedCalendarFilter: null,
			},
			this.getBookings,
		);
	};

	openTypeFilter = () => {
		this.setState({
			typeFilterOpen: true,
		});
	};

	closeTypeFilter = () => {
		this.setState({
			typeFilterOpen: false,
		});
	};

	selectTypeFilter = (type) => {
		this.setState(
			{
				selectedTypeFilter: type,
				typeFilterOpen: false,
			},
			this.getBookings,
		);
	};

	removeTypeFilter = (event) => {
		event.stopPropagation();
		this.setState(
			{
				selectedTypeFilter: null,
			},
			this.getBookings,
		);
	};

	userHasPermission = (user, permission) => {
		if (!user || !user.Roles) return false;

		// Find the user's role for the current site
		const userRole = user.Roles.find(
			(role) => role.site === this.props.auth.site,
		);
		if (!userRole) return false;

		// Master users have all permissions
		if (userRole.type === "master") return true;

		// Look up the user type in auth.userTypes to get permissions
		const userType = this.props.auth.userTypes.find(
			(ut) => ut.site === this.props.auth.site && ut.typeName === userRole.type,
		);
		if (!userType || !userType.permissions) return false;

		return userType.permissions.includes(permission);
	};

	filterCalendarsByPermissions = (calendars) => {
		// Users with 'bookings' permission see all calendars
		if (validateAccess(this.props.auth.site, "bookings", this.props.auth)) {
			return calendars;
		}

		// Users with only 'assignedBookings' permission see only calendars they admin
		if (
			validateAccess(this.props.auth.site, "assignedBookings", this.props.auth)
		) {
			return calendars.filter((calendar) => {
				return (
					calendar.AdminUsers &&
					calendar.AdminUsers.includes(this.props.auth.user.Id)
				);
			});
		}

		// Users with no permissions see no calendars
		return [];
	};

	getAccessibleCalendarIds = () => {
		const filteredCalendars = this.filterCalendarsByPermissions(
			this.props.calendars,
		);
		return filteredCalendars.map((calendar) => calendar.Id);
	};

	filterBookingsByPermissions = (bookings) => {
		// Users with 'bookings' permission see all bookings
		if (validateAccess(this.props.auth.site, "bookings", this.props.auth)) {
			return bookings;
		}

		// Users with only 'assignedBookings' permission see only bookings for calendars they admin
		if (
			validateAccess(this.props.auth.site, "assignedBookings", this.props.auth)
		) {
			const accessibleCalendarIds = this.getAccessibleCalendarIds();
			return bookings.filter((booking) => {
				return (
					booking.CalendarId &&
					accessibleCalendarIds.includes(booking.CalendarId)
				);
			});
		}

		// Users with no permissions see no bookings
		return [];
	};

	openTimeFilter = () => {
		this.setState({
			timeFilterOpen: true,
		});
	};

	closeTimeFilter = () => {
		this.setState(
			{
				timeFilterOpen: false,
			},
			this.getBookings,
		);
	};

	startDateChanged = (date) => {
		if (moment(date).isSameOrBefore(this.state.endDate)) {
			this.setState({ startDate: date });
		}
	};

	endDateChanged = (date) => {
		if (moment(date).isSameOrAfter(this.state.startDate)) {
			this.setState({ endDate: date });
		}
	};

	getTimeFilterText = () => {
		if (this.state.startDate && this.state.endDate) {
			return `Time: ${moment(this.state.startDate).format("DD/MM/YYYY")}  to  ${moment(this.state.endDate).format("DD/MM/YYYY")}`;
		}
		return "Time";
	};

	onHandleChange = (event) => {
		var stateChange = {};
		stateChange[event.target.getAttribute("id")] = event.target.value;
		this.setState(stateChange);
	};

	sortByCol(col) {
		if (this.state.sortColumn === col) {
			this.setState({
				sortDesc: !this.state.sortDesc,
			});
		} else {
			this.setState({
				sortColumn: col,
				sortDesc: false,
			});
		}
	}

	getStatusColor = (status) => {
		const STATUS_COLORS = {
			Confirmed: COLOUR_GREEN, // Green
			Pending: COLOUR_TANGERINE, // Orange
			Rejected: COLOUR_RED, // Blue
			Cancelled: COLOUR_RED, // Blue
		};
		return STATUS_COLORS[status] || STATUS_COLORS.Confirmed;
	};

	renderStatusBadge = (booking) => {
		const status = booking.Status || "Confirmed";
		return (
			<span
				style={{
					color: this.getStatusColor(status),
					fontSize: "14px",
					fontWeight: "500",
				}}
			>
				{status}
			</span>
		);
	};

	renderUserCell(booking) {
		// If user filter is active and this booking has the filtered user as a participant (not owner)
		if (this.state.selectedUserFilter && booking.isParticipant) {
			return (
				<td>
					<Text type="h5" style={{ marginTop: 4 }}>
						BOOKED BY:
					</Text>
					<UserListing size={15} user={booking.User} />
					<Text type="h5" style={{ marginTop: 4 }}>
						PARTICIPANT:
					</Text>
					<UserListing size={15} user={this.state.selectedUserFilter} />
				</td>
			);
		}

		// Default: show only the owner (no label)
		return (
			<td>
				<UserListing size={15} user={booking.User} />
			</td>
		);
	}

	renderItems(source) {
		return source.map((ev) => {
			return (
				<tr key={ev.Id}>
					<td className="table-TitleColumn">
						<Link to={`/bookings/booking/${ev.Id}`}>{ev.Id}</Link>
						{ev.Cancelled && (
							<>
								<br />
								<Text type="red">Cancelled</Text>
							</>
						)}
					</td>
					{this.renderUserCell(ev)}
					<td>{this.renderStatusBadge(ev)}</td>
					<td>{ev.Calendar.Title}</td>
					<td
						style={
							ev.BookingTypeId === "BLOCKOUT"
								? { color: COLOUR_RED }
								: undefined
						}
					>
						{ev.BookingType.Title}
					</td>
					<td>
						{moment.parseZone(ev.TimeString || ev.Time).format(TIMESTAMPFORMAT)}
					</td>
					<td>{minutesToString((ev.EndTime - ev.Time) / 60000)}</td>
				</tr>
			);
		});
	}

	renderSort(col) {
		if (col !== this.state.sortColumn) {
			return null;
		}
		return (
			<FontAwesome
				style={{ marginLeft: 5 }}
				name={this.state.sortDesc ? "chevron-up" : "chevron-down"}
			/>
		);
	}

	sortIsActive(col) {
		if (col !== this.state.sortColumn) {
			return "";
		}
		return " table--columnActive";
	}

	renderView(source) {
		return (
			<Table className="plussTable" striped bordered condensed hover>
				<thead>
					<tr>
						<th
							className={`${this.sortIsActive("Id")}`}
							style={{ cursor: "pointer", width: 120 }}
							onClick={() => {
								this.sortByCol("Id");
							}}
						>
							Booking ID{this.renderSort("Id")}
						</th>
						<th
							className={`${this.sortIsActive("User")}`}
							style={{ cursor: "pointer", width: 140 }}
							onClick={() => {
								this.sortByCol("User");
							}}
						>
							User{this.renderSort("User")}
						</th>
						<th
							className={`${this.sortIsActive("Status")}`}
							style={{ cursor: "pointer", width: 100 }}
							onClick={() => {
								this.sortByCol("Status");
							}}
						>
							Status{this.renderSort("Status")}
						</th>
						<th
							className={`${this.sortIsActive("Calendar")}`}
							style={{ cursor: "pointer", width: 120 }}
							onClick={() => {
								this.sortByCol("Calendar");
							}}
						>
							Calendar{this.renderSort("Calendar")}
						</th>
						<th
							className={`${this.sortIsActive("Type")}`}
							style={{ cursor: "pointer", width: 120 }}
							onClick={() => {
								this.sortByCol("Type");
							}}
						>
							Type{this.renderSort("Type")}
						</th>
						<th
							className={`${this.sortIsActive("Time")}`}
							style={{ cursor: "pointer", width: 120 }}
							onClick={() => {
								this.sortByCol("Time");
							}}
						>
							Time
						</th>
						<th style={{ width: 100 }}>Length</th>
					</tr>
				</thead>
				<tbody>{this.renderItems(source)}</tbody>
			</Table>
		);
	}

	renderEmpty() {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
					justifyContent: "center",
					alignItems: "center",
					marginTop: 32,
				}}
			>
				<div className="emptyState" />
				<div
					className="marginTop-32"
					style={{ maxWidth: 500, textAlign: "center" }}
				>
					<Text type="h3">There are no bookings</Text>
				</div>
			</div>
		);
	}

	openStatusFilter = () => {
		this.setState({ statusFilterOpen: true });
	};

	closeStatusFilter = () => {
		this.setState({ statusFilterOpen: false });
	};

	selectStatusFilter = (status) => {
		this.setState(
			{
				selectedStatusFilter: status,
				statusFilterOpen: false,
			},
			this.getBookings,
		);
	};

	removeStatusFilter = (event) => {
		event.stopPropagation();
		this.setState(
			{
				selectedStatusFilter: null,
			},
			this.getBookings,
		);
	};

	renderContent() {
		if (this.state.selectedView === "day") {
			return this.renderDayView();
		}
		if (
			this.state.selectedView === "calendar" ||
			this.state.selectedView === "week"
		) {
			return this.renderCalendar();
		}

		let source = _.sortBy(this.state.bookings, this.state.sortColumn);
		if (this.state.sortDesc) {
			source.reverse();
		}
		source = this.filterByStatus(source);
		if (_.isEmpty(source)) {
			return this.renderEmpty();
		}
		return this.renderView(source);
	}

	renderCalendar() {
		return (
			<BookingCalendar
				bookingList={this.getCalendarBookings()}
				selectDate={this.selectDate}
				selectedDate={this.state.selectedDate}
				view={this.state.selectedView}
				changeMonth={this.onChangeMonth}
				changeWeek={this.onChangeWeek}
			/>
		);
	}

	renderDayView() {
		return (
			<BookingDayView
				bookings={this.getCalendarBookings()}
				calendars={this.props.calendars}
				selectedDate={this.state.startDate}
				changeDay={this.onChangeDay}
				loading={this.state.loadingBookings}
			/>
		);
	}

	renderSelectedDate() {
		if (!this.state.selectedDate) {
			return null;
		}

		const dateBookings = this.getCalendarBookings().filter(
			(booking) => booking.DateKey === this.state.selectedDate.dateKey,
		);

		return (
			<Popup
				title={`Bookings for ${moment(this.state.selectedDate.dateKey, "DD-MM-YYYY").format("DD/MM/YYYY")}`}
				onClose={() => this.selectDate(null)}
				maxWidth={600}
				minWidth={400}
				hasPadding
			>
				{dateBookings.map((booking, index) => (
					<div
						key={index}
						style={{
							padding: "8px 0",
							borderBottom:
								index < dateBookings.length - 1 ? "1px solid #eee" : "none",
						}}
					>
						<Link
							to={`/bookings/booking/${booking.Id}`}
							style={{ textDecoration: "none" }}
						>
							<div>
								<Text
									type="h6"
									style={booking.IsBlockOut ? { color: COLOUR_RED } : undefined}
								>
									{booking.Id} - {booking.Location}
								</Text>
								<Text type="body">
									{booking.Time}
									{booking.EndTime ? ` - ${booking.EndTime}` : ""}
								</Text>
								{booking.Cancelled && (
									<Text type="body" style={{ color: COLOUR_RED }}>
										(Cancelled)
									</Text>
								)}
							</div>
						</Link>
					</div>
				))}
			</Popup>
		);
	}

	renderFilters() {
		let calendarFilter = (
			<Tag
				className="marginRight-10"
				onClick={this.openCalendarFilter}
				text="Calendar"
			/>
		);
		let typeFilter = (
			<Tag
				className="marginRight-10"
				onClick={this.openTypeFilter}
				text="Type"
			/>
		);
		let userFilter = (
			<Tag
				className="marginRight-10"
				onClick={this.openUserFilter}
				text="User"
			/>
		);
		let timeFilter = (
			<Tag
				className="marginRight-10"
				onClick={this.openTimeFilter}
				text={this.getTimeFilterText()}
			/>
		);
		let statusFilter = (
			<Tag
				className="marginRight-10"
				onClick={this.openStatusFilter}
				text="Status"
			/>
		);

		if (this.state.selectedUserFilter) {
			userFilter = (
				<Tag
					className="marginRight-10"
					onClick={this.openUserFilter}
					rightIcon="close"
					rightClick={this.removeUserFilter}
				>
					<UserListing
						size={15}
						user={this.state.selectedUserFilter}
						textClass="tag_text"
					/>
				</Tag>
			);
		}
		if (this.state.selectedCalendarFilter) {
			calendarFilter = (
				<Tag
					className="marginRight-10"
					onClick={this.openCalendarFilter}
					rightIcon="close"
					rightClick={this.removeCalendarFilter}
					text={this.state.selectedCalendarFilter.Title}
				/>
			);
		}
		if (this.state.selectedTypeFilter) {
			typeFilter = (
				<Tag
					className="marginRight-10"
					onClick={this.openTypeFilter}
					rightIcon="close"
					rightClick={this.removeTypeFilter}
					text={this.state.selectedTypeFilter.Title}
				/>
			);
		}
		if (this.state.selectedStatusFilter) {
			statusFilter = (
				<Tag
					className="marginRight-10"
					onClick={this.openStatusFilter}
					rightIcon="close"
					rightClick={this.removeStatusFilter}
					text={this.state.selectedStatusFilter}
				/>
			);
		}

		// Hide time filter in calendar/day views
		const showTimeFilter = this.state.selectedView === "list";
		// Hide calendar filter in day view (columns already represent calendars)
		const showCalendarFilter = this.state.selectedView !== "day";

		// Check if user has restricted permissions
		const hasRestrictedView =
			!validateAccess(this.props.auth.site, "bookings", this.props.auth) &&
			validateAccess(this.props.auth.site, "assignedBookings", this.props.auth);

		return (
			<div>
				<FilterMenu
					viewType={this.state.selectedView}
					updateView={this.updateView.bind(this)}
					showCalendar={true}
				/>
				{hasRestrictedView && (
					<div className="flex flex-center marginTop-10 marginBottom-10">
						<Text
							type="body"
							style={{ color: TEXT_LIGHT, fontStyle: "italic" }}
						>
							Showing only calendars you administer
						</Text>
					</div>
				)}
				<div className="flex flex-center marginTop-20">
					<Text type="h5" className="marginRight-20">
						Filter by
					</Text>
					{showTimeFilter && timeFilter}
					{statusFilter}
					{showCalendarFilter && calendarFilter}
					{typeFilter}
					{userFilter}
				</div>
			</div>
		);
	}

	renderUserFilterPopup() {
		if (!this.state.userFilterOpen) {
			return null;
		}
		return (
			<Popup
				title="Select User"
				maxWidth={600}
				minWidth={400}
				minHeight={400}
				hasPadding
				onClose={this.closeUserFilter}
			>
				<GenericInput
					id="userSearch"
					type="text"
					label="Search"
					placeholder="Enter name"
					value={this.state.userSearch}
					onChange={(e) => this.onHandleChange(e)}
					alwaysShowLabel
				/>
				{_.sortBy(this.props.users, (u) => u.displayName.toUpperCase())
					.filter((u) => {
						if (_.isEmpty(this.state.userSearch)) return true;
						return (
							u.displayName
								.toUpperCase()
								.indexOf(this.state.userSearch.toUpperCase()) > -1
						);
					})
					.map((user) => {
						return (
							<UserListing
								key={user.userId}
								user={user}
								onClick={() => {
									this.selectUserFilter(user);
								}}
							/>
						);
					})}
			</Popup>
		);
	}

	renderTypeFilterPopup() {
		if (!this.state.typeFilterOpen) {
			return null;
		}
		return (
			<Popup
				title="Select Type"
				maxWidth={600}
				minWidth={400}
				hasPadding
				onClose={this.closeTypeFilter}
			>
				{[
					{ Id: "BLOCKOUT", Title: "Block Out" },
					...this.props.bookingTypes,
				].map((bt) => {
					return (
						<Tag
							onClick={() => {
								this.selectTypeFilter(bt);
							}}
							text={bt.Title}
							className="marginRight-10"
						/>
					);
				})}
			</Popup>
		);
	}

	renderCalendarFilterPopup() {
		if (!this.state.calendarFilterOpen) {
			return null;
		}

		// Only show calendars the user has permission to access
		const accessibleCalendars = this.filterCalendarsByPermissions(
			this.props.calendars,
		);

		return (
			<Popup
				title="Select Calendar"
				maxWidth={600}
				minWidth={400}
				hasPadding
				onClose={this.closeCalendarFilter}
			>
				{accessibleCalendars.map((bc) => {
					return (
						<Tag
							key={bc.Id}
							onClick={() => {
								this.selectCalendarFilter(bc);
							}}
							text={bc.Title}
							className="marginRight-10"
						/>
					);
				})}
			</Popup>
		);
	}

	renderTimeFilterPopup() {
		if (!this.state.timeFilterOpen) {
			return null;
		}
		return (
			<Popup
				title="Select Time"
				maxWidth={600}
				minWidth={400}
				hasPadding
				onClose={this.closeTimeFilter}
			>
				<div className="analyticsFilter__dateRange">
					<DatePicker
						hideTop
						selectedDate={this.state.startDate}
						selectDate={this.startDateChanged}
					/>
					<DatePicker
						hideTop
						selectedDate={this.state.endDate}
						selectDate={this.endDateChanged}
					/>
				</div>
			</Popup>
		);
	}

	renderStatusFilterPopup() {
		if (!this.state.statusFilterOpen) {
			return null;
		}

		const statuses = ["Confirmed", "Pending", "Rejected", "Cancelled"];

		return (
			<Popup
				title="Select Status"
				maxWidth={600}
				minWidth={400}
				hasPadding
				onClose={this.closeStatusFilter}
			>
				{statuses.map((status) => {
					return (
						<Tag
							key={status}
							onClick={() => {
								this.selectStatusFilter(status);
							}}
							text={status}
							className="marginRight-10"
						/>
					);
				})}
			</Popup>
		);
	}

	render() {
		return (
			<div style={{ minWidth: "100%" }}>
				{this.renderSelectedDate()}
				{this.renderCalendarFilterPopup()}
				{this.renderTypeFilterPopup()}
				{this.renderStatusFilterPopup()}
				{this.renderUserFilterPopup()}
				{this.renderTimeFilterPopup()}
				{this.renderFilters()}
				{this.renderContent()}
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	const { calendars, types } = state.bookings;
	const { users } = state.users;
	const { auth } = state;
	return {
		users,
		calendars,
		auth,
		bookingTypes: types,
	};
};

export default connect(mapStateToProps, {
	calendarsLoaded,
	bookingTypesLoaded,
	usersLoaded,
})(withRouter(Bookings));
